"use client";

import * as React from "react";
import { createPortal } from "react-dom";
import { cn } from "@/utils/cn";
import { SelectOption } from "./single-select";
import { Check, ChevronDown, SearchIcon } from "lucide-react";
import { Input } from "@ui/input";

type BaseProps<T extends string | number> = {
  options: SelectOption<T>[];
  placeholder?: string;
  disabled?: boolean;
  readonly?: boolean;
  hasError?: boolean;
  className?: string;
  name?: string;
  onBlur?: () => void;
  onSearchChange?: (search: string) => void;
  onLoadMore?: () => void;
  isFetchingMore?: boolean;
  filterOptionsOnClient?: boolean;
};

type SingleModeProps<T extends string | number = string> = BaseProps<T> & {
  value?: T;
  onChange?: (value: T) => void;
  mode?: "single";
  multiple?: false;
};

type MultiModeProps<T extends string | number = string> = BaseProps<T> & {
  value?: T[];
  onChange?: (value: T[]) => void;
  mode?: "multiple";
  multiple?: true;
};

export type SearchableSelectProps<T extends string | number = string> =
  | SingleModeProps<T>
  | MultiModeProps<T>;

const normalizeOptionValue = (value: string | number | undefined) =>
  String(value ?? "").trim();

function isMultiMode<T extends string | number>(
  props: SearchableSelectProps<T>,
): props is MultiModeProps<T> {
  return props.mode === "multiple" || props.multiple === true;
}

export function SearchableSelect<T extends string | number = string>(
  props: SearchableSelectProps<T>,
) {
  const {
    options,
    placeholder = "Select an option",
    disabled,
    readonly,
    hasError,
    className,
    name,
    onBlur,
    onSearchChange,
    onLoadMore,
    isFetchingMore,
    filterOptionsOnClient = true,
  } = props;
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const triggerRef = React.useRef<HTMLButtonElement | null>(null);
  const dropdownRef = React.useRef<HTMLDivElement | null>(null);
  const listRef = React.useRef<HTMLDivElement | null>(null);
  const searchInputRef = React.useRef<HTMLInputElement | null>(null);
  const [dropdownStyle, setDropdownStyle] = React.useState<React.CSSProperties>(
    {
      position: "fixed",
      left: 0,
      top: 0,
      width: 0,
      zIndex: 120,
      visibility: "hidden",
    },
  );

  const updateDropdownPosition = React.useCallback(() => {
    if (!triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const dropdownHeight = 288;
    const spaceBelow = viewportHeight - rect.bottom;
    const shouldOpenUpward =
      spaceBelow < dropdownHeight && rect.top > dropdownHeight;

    setDropdownStyle({
      position: "fixed",
      left: rect.left,
      top: shouldOpenUpward ? rect.top - 8 : rect.bottom + 8,
      width: rect.width,
      zIndex: 120,
      transform: shouldOpenUpward ? "translateY(-100%)" : undefined,
      visibility: "visible",
    });
  }, []);

  const handleListScroll = React.useCallback(() => {
    if (!listRef.current || !onLoadMore) return;
    const { scrollTop, scrollHeight, clientHeight } = listRef.current;
    if (scrollHeight - scrollTop - clientHeight < 60) {
      onLoadMore();
    }
  }, [onLoadMore]);

  const filtered = React.useMemo(() => {
    if (!filterOptionsOnClient || !search.trim()) return options;
    const term = search.toLowerCase();
    return options.filter(
      (opt) =>
        String(opt.label).toLowerCase().includes(term) ||
        String(opt.value).toLowerCase().includes(term),
    );
  }, [filterOptionsOnClient, options, search]);

  const selectedValues = React.useMemo<T[]>(() => {
    if (isMultiMode(props)) {
      return props.value ?? [];
    }
    return props.value !== undefined ? [props.value] : [];
  }, [props.mode, props.multiple, props.value]);

  const selectedLabels = React.useMemo(() => {
    if (isMultiMode(props)) {
      const selectedSet = new Set(
        ((props.value ?? []) as T[]).map((val) => normalizeOptionValue(val)),
      );
      return options
        .filter((opt) => selectedSet.has(normalizeOptionValue(opt.value)))
        .map((opt) => opt.label);
    }
    const singleValue = props.value;
    return singleValue === undefined
      ? []
      : options
          .filter(
            (opt) =>
              normalizeOptionValue(opt.value) ===
              normalizeOptionValue(singleValue),
          )
          .map((opt) => opt.label);
  }, [options, props.mode, props.multiple, props.value]);

  React.useEffect(() => {
    const selected = isMultiMode(props)
      ? ((props.value ?? []) as T[]).map((value) => normalizeOptionValue(value))
      : props.value !== undefined
        ? [normalizeOptionValue(props.value)]
        : [];
    const selectedValuesWithContent = selected.filter(Boolean);
    if (
      selectedValuesWithContent.length === 0 ||
      selectedLabels.length > 0
    ) {
      return;
    }

    console.warn("[SearchableSelectDebug] selected value has no label", {
      name,
      value: props.value,
      selectedValues: selectedValuesWithContent,
      optionCount: options.length,
      optionValuesPreview: options
        .slice(0, 20)
        .map((option) => normalizeOptionValue(option.value)),
      optionPreview: options.slice(0, 5).map((option) => ({
        value: option.value,
        label: String(option.label),
      })),
    });
  }, [name, options, props.mode, props.multiple, props.value, selectedLabels]);

  const resetSearch = React.useCallback(() => {
    setSearch("");
    onSearchChange?.("");
  }, [onSearchChange]);

  const closeDropdown = React.useCallback(() => {
    setOpen(false);
    resetSearch();
  }, [resetSearch]);

  const toggleOpen = () => {
    if (disabled || readonly) return;
    setOpen((prev) => {
      if (prev) resetSearch();
      return !prev;
    });
  };

  const handleSelect = (selected: T) => {
    if (disabled || readonly) return;
    if (isMultiMode(props)) {
      const currentValues = props.value ?? [];
      const exists = currentValues.some(
        (val) => normalizeOptionValue(val) === normalizeOptionValue(selected),
      );
      const nextValues = exists
        ? currentValues.filter(
            (val) =>
              normalizeOptionValue(val) !== normalizeOptionValue(selected),
          )
        : [...currentValues, selected];
      props.onChange?.(nextValues);
      return;
    }

    props.onChange?.(selected);
    closeDropdown();
  };

  React.useEffect(() => {
    if (disabled || readonly) {
      closeDropdown();
    }
  }, [closeDropdown, disabled, readonly]);

  React.useLayoutEffect(() => {
    if (!open) return;

    updateDropdownPosition();
    requestAnimationFrame(() => {
      searchInputRef.current?.focus();
    });

    const handleViewportChange = () => updateDropdownPosition();

    window.addEventListener("resize", handleViewportChange);
    window.addEventListener("scroll", handleViewportChange, true);

    return () => {
      window.removeEventListener("resize", handleViewportChange);
      window.removeEventListener("scroll", handleViewportChange, true);
    };
  }, [open, updateDropdownPosition]);

  React.useEffect(() => {
    const handler = (event: MouseEvent) => {
      const target = event.target as Node;
      const clickedTrigger = containerRef.current?.contains(target);
      const clickedDropdown = dropdownRef.current?.contains(target);

      if (!clickedTrigger && !clickedDropdown) {
        closeDropdown();
        onBlur?.();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [closeDropdown, onBlur]);

  return (
    <div ref={containerRef} className={cn("relative w-full", className)}>
      <button
        ref={triggerRef}
        type="button"
        onClick={toggleOpen}
        disabled={disabled}
        aria-readonly={readonly}
        className={cn(
          "flex h-12 w-full items-center justify-between rounded-[6px] border border-stroke bg-white px-4 text-left",
          "text-base font-normal text-dark",
          "focus:border-[1.5px] focus:border-giant-blue focus:outline-none",
          disabled &&
            "bg-gray-2 text-primary-text border-gray-2 cursor-not-allowed",
          readonly && "cursor-default",
          hasError && "border-[1.5px] border-red focus:border-red",
          className,
        )}
      >
        <span
          className={cn(
            "flex-1 truncate text-left",
            selectedLabels.length === 0 && "text-secondary-text",
          )}
        >
          {selectedLabels.length > 0 ? selectedLabels.join(", ") : placeholder}
        </span>
        <ChevronDown className="h-4 w-4 text-secondary-text" />
      </button>

      {open &&
        createPortal(
          <div
            ref={dropdownRef}
            style={dropdownStyle}
            className="rounded-[6px] border border-stroke bg-white shadow-md"
            onMouseDown={(event) => event.stopPropagation()}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="p-2">
              <Input
                ref={searchInputRef}
                value={search}
                icon={<SearchIcon className="w-5 h-5 text-gray-400" />}
                onChange={(e) => {
                  if (readonly) return;
                  setSearch(e.target.value);
                  onSearchChange?.(e.target.value);
                }}
                placeholder="Search an options"
                autoFocus
                readOnly={readonly}
                disabled={disabled}
              />
            </div>
            <div
              ref={listRef}
              className="max-h-60 overflow-auto"
              onScroll={handleListScroll}
            >
              {filtered.map((option) => (
                <button
                  key={String(option.value)}
                  type="button"
                  className={cn(
                    "flex w-full items-center px-4 py-2 text-left text-base text-dark hover:bg-gray-1",
                    selectedValues.some(
                      (val) =>
                        normalizeOptionValue(val) ===
                        normalizeOptionValue(option.value),
                    ) && "bg-giant-blue/10",
                  )}
                  onClick={() => handleSelect(option.value)}
                >
                  {isMultiMode(props) && (
                    <span
                      className={cn(
                        "mr-2 flex h-4 w-4 shrink-0 items-center justify-center rounded border border-stroke",
                        selectedValues.some(
                          (val) =>
                            normalizeOptionValue(val) ===
                            normalizeOptionValue(option.value),
                        ) && "border-giant-blue bg-giant-blue text-white",
                      )}
                    >
                      {selectedValues.some(
                        (val) =>
                          normalizeOptionValue(val) ===
                          normalizeOptionValue(option.value),
                      ) && <Check className="h-3 w-3" />}
                    </span>
                  )}
                  <span>{option.label}</span>
                </button>
              ))}
              {filtered.length === 0 && !isFetchingMore && (
                <div className="px-4 py-3 text-sm text-secondary-text">
                  No options
                </div>
              )}
              {isFetchingMore && (
                <div className="px-4 py-3 text-sm text-secondary-text text-center">
                  Loading...
                </div>
              )}
            </div>
          </div>,
          document.body,
        )}

      {name &&
        (isMultiMode(props)
          ? selectedValues.map((val, idx) => (
              <input key={idx} type="hidden" name={name} value={String(val)} />
            ))
          : props.value !== undefined && (
              <input type="hidden" name={name} value={String(props.value)} />
            ))}
    </div>
  );
}
